import { CreateComponentPublicInstance } from 'vue';
import { TLiftSettingsInfinityAddonConfig } from '@llws/platform-shared-types/types/db/lift';
import { TSelectOptionWithStringValue } from '@/components/RsSidebarFilters.vue';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { getCurrentClientInfinityAddonConfig } from '@/shared/helpers/infinity-addon-config';
import { getPremiumReportingCities } from '@/shared/services/sisense';

export interface IInfinityPLProperty {
  buildingId: number;
  clientId: number;
  buildingName: string;
  cityId: number;
  cityName: string;
  provinceName: string;
  buildingTypeId: string;
  buildingTypeName: string;
  startDate: string;
  expiryDate: string | null;
  tier: string;
  tierOrder: number;
}

let cachedEligibleProperties: IInfinityPLProperty[] | null = null;
let cachedMarketInsightsThreshold: { value: number | null } | null = null;

/**
 * Reads the current client's Infinity addon config and returns the `marketInsights` threshold value.
 * The threshold determines the minimum `tierOrder` a property must have to be eligible.
 */
export const getInfinityPLMarketInsightsThreshold = async (clientId: number): Promise<number | null> => {
  if (cachedMarketInsightsThreshold !== null) {
    return cachedMarketInsightsThreshold.value;
  }

  const parsed: TLiftSettingsInfinityAddonConfig | null = getCurrentClientInfinityAddonConfig(clientId);

  if (!parsed) {
    cachedMarketInsightsThreshold = { value: null };
    return null;
  }

  const rawThreshold = parsed.marketInsights;

  if (rawThreshold === null || rawThreshold === undefined || Number.isNaN(Number(rawThreshold))) {
    cachedMarketInsightsThreshold = { value: null };
    return null;
  }

  cachedMarketInsightsThreshold = { value: Number(rawThreshold) };
  return cachedMarketInsightsThreshold.value;
};

/**
 * Fetches infinity promoted listing properties for the current client and returns only
 * unique properties (by buildingId) in markets (cities) where at least one property has
 * `tierOrder >= marketInsights` threshold from lift settings.
 * Results are cached per session; call `clearInfinityPLCache()` on client change.
 */
export const getInfinityPLEligibleProperties = async (
  clientId: number,
): Promise<IInfinityPLProperty[]> => {
  if (cachedEligibleProperties !== null) {
    return cachedEligibleProperties;
  }

  const threshold = await getInfinityPLMarketInsightsThreshold(clientId);

  // `marketInsights: null` means this feature is disabled for the client.
  if (threshold === null) {
    cachedEligibleProperties = [];
    return [];
  }

  try {
    const response = await adminRequest.get<IInfinityPLProperty[]>(
      `/client-api/infinity-promoted-listings`,
      `?clientIds=${clientId}&subState=active`,
    );

    const allProperties = response?.data ?? [];
    const tierQualifiedProperties = allProperties.filter((p) => p.tierOrder >= threshold);

    // A market is eligible when it has at least one property meeting the tier threshold.
    const eligibleCityIds = new Set(
      tierQualifiedProperties.map((p) => p.cityId),
    );

    if (!eligibleCityIds.size) {
      cachedEligibleProperties = [];
      return [];
    }

    // Deduplicate by buildingId — the endpoint may return duplicates across subscription types
    const seen = new Set<number>();
    const properties = allProperties.filter((p) => {
      if (!eligibleCityIds.has(p.cityId) || seen.has(p.buildingId)) {
        return false;
      }
      seen.add(p.buildingId);
      return true;
    });

    cachedEligibleProperties = properties;
    return properties;
  } catch (err) {
    console.error('Failed to fetch infinity PL properties:', err);
    cachedEligibleProperties = [];
    return [];
  }
};

/**
 * Clears cached data. Should be called when the active client changes.
 */
export const clearInfinityPLCache = () => {
  cachedEligibleProperties = null;
  cachedMarketInsightsThreshold = null;
};

/**
 * Checks if the current client qualifies for the Infinity PL Portfolio Comparison page.
 * Returns `true` when:
 * 1. The client does NOT have the `premium_reporting` product key.
 * 2. At least one property has `tierOrder >= marketInsights` threshold from lift settings.
 */
export const hasInfinityPLMarketInsights = async (
  vm: CreateComponentPublicInstance,
): Promise<boolean> => {
  const hasPremiumReporting = vm.$rsGlobals.$currentUser?.hasPermission('premium_reporting');
  if (hasPremiumReporting) {
    return false;
  }

  const clientId = vm.$rsGlobals.$currentClient?.id;
  if (!clientId) {
    return false;
  }

  const eligibleProperties = await getInfinityPLEligibleProperties(clientId);
  return eligibleProperties.length > 0;
};

/**
 * Returns property select options for eligible infinity PL properties.
 * Used by the Infinity Portfolio Comparison property filter sidebar control.
 *
 * @param rentsyncCityIds - Optional set of Rentsync city IDs to scope properties.
 *   When provided, only properties in those cities are returned.
 */
export const getInfinityPLPropertyOptions = async (
  clientId: number,
  rentsyncCityIds?: Set<number>,
): Promise<TSelectOptionWithStringValue[]> => {
  let filteredProperties = await getInfinityPLEligibleProperties(clientId);

  if (rentsyncCityIds?.size) {
    filteredProperties = filteredProperties.filter((p) => rentsyncCityIds.has(p.cityId));
  }

  return filteredProperties.map((p) => ({
    text: p.buildingName,
    value: `${p.buildingId}`,
  }));
};

/**
 * Extracts the Rentsync city ID from a Sisense city select option.
 * The Rentsync ID is stored in `subText` when available, otherwise falls back to `value`.
 */
export const toRentsyncCityId = (city: TSelectOptionWithStringValue): number => {
  const rawId = ('subText' in city ? city.subText : city.value) ?? city.value;
  const parsed = Number(rawId);
  return Number.isNaN(parsed) ? -1 : parsed;
};

/**
 * Returns city select options derived from eligible infinity PL properties,
 * mapped to Sisense analytics city IDs via the `premium-reporting-fsas` lookup.
 *
 * Mapping strategy:
 * 1. First attempts a filtered lookup by the eligible Rentsync city IDs.
 * 2. If some cities are not returned, falls back to the full mapping table.
 * 3. Only cities that have a valid Sisense analytics ID are included.
 */
export const getInfinityPLCityOptions = async (
  clientId: number,
): Promise<TSelectOptionWithStringValue[]> => {
  const properties = await getInfinityPLEligibleProperties(clientId);
  const uniqueCityIds = [...new Set(properties.map((p) => p.cityId))];
  const cityIdSet = new Set(uniqueCityIds.map((id) => Number(id)));

  if (!uniqueCityIds.length) {
    return [];
  }

  // Step 1: filtered lookup by eligible city IDs
  const filteredSisenseCities = await getPremiumReportingCities(uniqueCityIds, true);
  let sisenseCities: TSelectOptionWithStringValue[] = filteredSisenseCities ?? [];

  // Step 2: fallback to full mapping table when filtered lookup under-returns
  if (sisenseCities.length < uniqueCityIds.length) {
    const allSisenseCities = await getPremiumReportingCities(undefined, true);
    const fallbackCities = (allSisenseCities ?? [])
      .filter((city) => cityIdSet.has(toRentsyncCityId(city)));

    // Merge and deduplicate by analytics city ID
    const mergedByAnalyticsId = new Map<string, TSelectOptionWithStringValue>();

    for (const city of [...sisenseCities, ...fallbackCities]) {
      if (city.value) {
        mergedByAnalyticsId.set(`${city.value}`, city);
      }
    }

    sisenseCities = [...mergedByAnalyticsId.values()];
  }

  return sisenseCities
    .filter((c) => c.value)
    .sort((a, b) => a.text.localeCompare(b.text));
};
